import {
  focusVisibleSelector
} from "../chunks/chunk.ZX7YPA4S.js";
import {
  hasSlot
} from "../chunks/chunk.Z2CDDKXX.js";
import "../chunks/chunk.AREYZEAJ.js";
import {
  __decorateClass,
  component_styles_default,
  theme_styles_default
} from "../chunks/chunk.U4VUMZVF.js";

// src/components/badge/badge.ts
import { LitElement, html } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

// src/components/badge/badge.styles.ts
import { css } from "lit";
var badge_styles_default = css`
	${theme_styles_default}
	${component_styles_default}

	:host {
		display: inline-flex;
	}

	.badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-family: var(--yotpo-font-family-text-small);
		font-weight: var(--yotpo-font-weight-text-small-regular);
		font-size: var(--yotpo-font-size-text-small);
		line-height: var(--yotpo-line-height-text-small);
		border-radius: var(--yotpo-border-radius-x-large);
		white-space: nowrap;
		padding: var(--yotpo-spacing-tiny) var(--yotpo-spacing-medium);
		user-select: none;
		cursor: inherit;
		min-width: 20px; /* 1.25rem */
		height: 20px; /* 1.25rem */
		border: var(--yotpo-button-border) transparent;
		transition: var(--yotpo-transition-medium) border-color, var(--yotpo-transition-medium) background-color,
			var(--yotpo-transition-medium) color, var(--yotpo-transition-medium) box-shadow;
	}

	.badge--clickable {
		cursor: pointer;
	}

	.badge--clickable${focusVisibleSelector} {
		box-shadow: var(--yotpo-focus-global);
	}

	.badge::-moz-focus-inner {
		border: 0;
	}

	.badge:focus {
		outline: none;
	}

	/*
   * Priority
   */

	/* Primary */
	.badge--priority-primary {
		color: var(--yotpo-color-white);
	}

	.badge--priority-primary.badge--neutral {
		background-color: var(--yotpo-color-general-70);
	}

	.badge--priority-primary.badge--primary {
		background-color: var(--yotpo-color-brand-70);
	}

	.badge--priority-primary.badge--warning {
		background-color: var(--yotpo-color-warning-70);
		color: var(--yotpo-color-general-100);
	}

	.badge--priority-primary.badge--danger {
		background-color: var(--yotpo-color-destructive-70);
	}

	.badge--priority-primary.badge--success {
		background-color: var(--yotpo-color-success-70);
	}

	.badge--priority-primary.badge--premium {
		background-color: var(--yotpo-color-premium-70);
	}

	/* Secondary */
	.badge--priority-secondary {
		color: var(--yotpo-font-color-light-primary);
	}

	.badge--priority-secondary.badge--neutral {
		background-color: var(--yotpo-color-general-30);
	}

	.badge--priority-secondary.badge--primary {
		background-color: var(--yotpo-color-brand-30);
	}

	.badge--priority-secondary.badge--warning {
		background-color: var(--yotpo-color-warning-30);
	}

	.badge--priority-secondary.badge--danger {
		background-color: var(--yotpo-color-destructive-30);
	}

	.badge--priority-secondary.badge--success {
		background-color: var(--yotpo-color-success-30);
	}

	.badge--priority-secondary.badge--premium {
		background-color: var(--yotpo-color-premium-30);
	}

	/* Tertiary */
	.badge--priority-tertiary {
		color: var(--yotpo-font-color-light-secondary);
		background-color: transparent;
	}

	.badge__tertiary-status {
		width: 10px; /* 0.625rem */
		height: 10px; /* 0.625rem */
		border-radius: var(--yotpo-border-radius-pill);
		border: 2px solid; /* 0.125rem */
		box-sizing: border-box;
		margin-right: var(--yotpo-icon-spacing);
	}

	.badge--neutral .badge__tertiary-status {
		background-color: var(--yotpo-color-general-70);
		border-color: var(--yotpo-color-general-30);
	}

	.badge--primary .badge__tertiary-status {
		background-color: var(--yotpo-color-brand-70);
		border-color: var(--yotpo-color-brand-30);
	}

	.badge--warning .badge__tertiary-status {
		background-color: var(--yotpo-color-warning-70);
		border-color: var(--yotpo-color-warning-30);
	}

	.badge--danger .badge__tertiary-status {
		background-color: var(--yotpo-color-destructive-70);
		border-color: var(--yotpo-color-destructive-30);
	}

	.badge--success .badge__tertiary-status {
		background-color: var(--yotpo-color-success-70);
		border-color: var(--yotpo-color-success-30);
	}

	.badge--premium .badge__tertiary-status {
		background-color: var(--yotpo-color-premium-70);
		border-color: var(--yotpo-color-premium-30);
	}

	/*
   * Prefix and Suffix
   */

	.badge:not(.badge--has-prefix) .badge__prefix {
		display: none;
	}

	.badge:not(.badge--has-suffix) .badge__suffix {
		display: none;
	}

	/* Clicks on icons shouldn't prevent the badge from gaining focus */
	.badge::slotted(yotpo-icon) {
		pointer-events: none;
	}

	.badge__prefix,
	.badge__suffix {
		align-items: center;
		height: var(--yotpo-icon-size);
		font-size: var(--yotpo-icon-size);
		flex-shrink: 0;
	}

	.badge--has-prefix .badge__prefix {
		padding-right: var(--yotpo-icon-spacing);
	}

	.badge--has-suffix .badge__suffix {
		padding-left: var(--yotpo-icon-spacing);
	}
`;

// src/components/badge/badge.ts
var YotpoBadge = class extends LitElement {
  constructor() {
    super(...arguments);
    this.hasPrefix = false;
    this.hasSuffix = false;
    this.priority = "primary" /* primary */;
    this.status = "neutral" /* neutral */;
    this.clickable = false;
  }
  connectedCallback() {
    super.connectedCallback();
    this.handleSlotChange();
  }
  handleSlotChange() {
    this.hasPrefix = hasSlot(this, "prefix", "yotpo-icon");
    this.hasSuffix = hasSlot(this, "suffix", "yotpo-icon");
  }
  click() {
    this.badge.click();
  }
  handleClick(event) {
    if (!this.clickable) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
  render() {
    return html`
			<span
				part="base"
				class=${classMap({
      badge: true,
      "badge--priority-primary": this.priority === "primary" /* primary */,
      "badge--priority-secondary": this.priority === "secondary" /* secondary */,
      "badge--priority-tertiary": this.priority === "tertiary" /* tertiary */,
      "badge--neutral": this.status === "neutral" /* neutral */,
      "badge--primary": this.status === "primary" /* primary */,
      "badge--warning": this.status === "warning" /* warning */,
      "badge--danger": this.status === "danger" /* danger */,
      "badge--success": this.status === "success" /* success */,
      "badge--premium": this.status === "premium" /* premium */,
      "badge--has-prefix": this.hasPrefix,
      "badge--has-suffix": this.hasSuffix,
      "badge--clickable": this.clickable
    })}
				role="status"
				tabindex=${this.clickable ? "0" : "-1"}
				@click=${(event) => this.handleClick(event)}
			>
				<span part="prefix" class="badge__prefix">
					<slot @slotchange=${this.handleSlotChange} name="prefix"></slot>
				</span>

				${this.priority === "tertiary" /* tertiary */ ? html`<div class="badge__tertiary-status"></div>` : ""}

				<span part="content" class="badge__content">
					<slot @slotchange=${this.handleSlotChange}></slot>
				</span>

				<span part="suffix" class="badge__suffix">
					<slot @slotchange=${this.handleSlotChange} name="suffix"></slot>
				</span>
			</span>
		`;
  }
};
YotpoBadge.styles = badge_styles_default;
__decorateClass([
  query(".badge")
], YotpoBadge.prototype, "badge", 2);
__decorateClass([
  state()
], YotpoBadge.prototype, "hasPrefix", 2);
__decorateClass([
  state()
], YotpoBadge.prototype, "hasSuffix", 2);
__decorateClass([
  property({ reflect: true })
], YotpoBadge.prototype, "priority", 2);
__decorateClass([
  property({ reflect: true })
], YotpoBadge.prototype, "status", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoBadge.prototype, "clickable", 2);
YotpoBadge = __decorateClass([
  customElement("yotpo-badge")
], YotpoBadge);
export {
  YotpoBadge
};
